import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Heading1, Stack } from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getFragment } from '../../graphql';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    GetPrescriberOrderDocument,
    SendOfferDocument,
} from '../../graphql/generated/graphql';
import type { OfferFragment } from '../../types';
import { getAbsoluteRoute } from '../../util';
import { hasPHX502 } from '../../util/offers/sort';
import { PharmacyDoesNotCarryMedicationAlert } from '../common/PharmacyDoesNotCarryMedicationAlert';
import { GenericSwapPricingDesc } from '../common/pricing/GenericSwapPricingDesc';
import { Pricing } from '../common/pricing/Pricing';

import { PatientProductDetails } from './components/PatientProductDetails';
import { PharmacyDoesNotCarryConfirmationModal } from './components/PharmacyDoesNotCarryConfirmationModal';
import { PharmacyProviderDetails } from './components/PharmacyProviderDetails';

export type PreviewDetailsProps = {
    offer: OfferFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const PreviewDetails = ({
    offer,
    prescriberOrder,
}: PreviewDetailsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isOpen, { open, close }] = useDisclosure(false);

    const generic = searchParams.get('generic');
    const genericName = searchParams.get('genericName');
    const brandName = searchParams.get('brandName');

    const patient = getFragment(prescriberOrder.patient);

    const pharmacyDoesNotCarryMedication = hasPHX502(offer);

    const [getUpdatedPrescriberOrder] = useLazyQuery(
        GetPrescriberOrderDocument,
        {
            fetchPolicy: 'network-only',
        }
    );

    const [sendOffer, { data, loading, error, called }] = useMutation(
        SendOfferDocument,
        {
            onCompleted: () => {
                setTimeout(() => {
                    getUpdatedPrescriberOrder({
                        variables: { prescriberOrderId: prescriberOrder.id },
                    });
                }, 10000);
            },
        }
    );

    const handleButtonClick = () => {
        if (pharmacyDoesNotCarryMedication) {
            open();
        } else {
            sendOffer({ variables: { offerId: offer.id } });
        }
    };

    const handleChooseDifferentPharmacy = () => {
        navigate(
            getAbsoluteRoute('cabinet.rxId.root', {
                prescriptionId: prescriberOrder.id,
            })
        );
    };

    if (called && error && !loading && !data?.sendOffer?.success) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (!loading && data?.sendOffer?.success) {
        return (
            <Navigate
                to={{
                    pathname: getAbsoluteRoute(
                        'orders.prescriberOrderId.confirmation',
                        {
                            prescriberOrderId: prescriberOrder.id,
                        }
                    ),
                    search: searchParams.toString(),
                }}
            />
        );
    }

    return (
        <Stack gap="lg">
            {!generic && (
                <PatientProductDetails
                    patientName={patient.fullName}
                    productDescription={prescriberOrder.productDescription}
                    quantity={offer.quantity}
                    prescriberOrder={prescriberOrder}
                />
            )}
            <Stack gap="xxl">
                <Stack gap="md">
                    <Heading1>{t('previewDetails.title')}</Heading1>
                    {pharmacyDoesNotCarryMedication && (
                        <PharmacyDoesNotCarryMedicationAlert />
                    )}
                    {!pharmacyDoesNotCarryMedication && (
                        <>
                            {generic && (
                                <GenericSwapPricingDesc
                                    brandName={brandName!}
                                    genericName={genericName!}
                                />
                            )}
                            <Pricing offer={offer} />
                        </>
                    )}
                </Stack>

                <PharmacyProviderDetails offer={offer} />
            </Stack>
            <Button size="lg" loading={loading} onClick={handleButtonClick}>
                {t('previewDetails.sendToPharmacy')}
            </Button>
            <PharmacyDoesNotCarryConfirmationModal
                isOpen={isOpen}
                close={close}
                onChooseDifferentPharmacy={handleChooseDifferentPharmacy}
                onSendToPharmacy={() =>
                    sendOffer({ variables: { offerId: offer.id } })
                }
                loading={loading}
            />
        </Stack>
    );
};
